import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiCopy, FiCheck, FiAlertTriangle, FiLoader, FiCode, FiShare2 } from 'react-icons/fi';
import ModalBase from '../Modal/ModalBase';
import { ShareService } from '../../services/shareService';
import { SHARE_WARNING, LIVE_SHARE_WARNING, buildShareUrl, buildEmbedSnippet } from '../../utils/shareConversation';
import { usePostHogCapture } from '../../hooks/usePostHogCapture';
import { POSTHOG_EVENTS } from '../../utils/posthogEvents';
import type { RootState } from '../../store';

interface ShareLinkModalProps {
  // Live scheduleRunner embed mode: pass a runnerId (shows public link + iframe embed snippet).
  runnerId?: string;
  // Single-message share mode: pass conversationId + messageId (shows the public link only).
  conversationId?: string;
  messageId?: string;
  onClose: () => void;
}

// Share modal with two modes, rendered through ModalGlobal/ModalBase (mounts only while open): - scheduleRunner embed (runnerId): public link + a copy-paste iframe snippet that refreshes live. - single message (conversationId + messageId): public link only. It creates the share on mount, then shows the link(s). Open via dispatch(setModalState({ modalView: modalViewsConst.shareEmbed, properties: { __modalSize: 'sm', ... } })).
const ShareLinkModal: React.FC<ShareLinkModalProps> = ({ runnerId, conversationId, messageId, onClose }) => {
  const { capture } = usePostHogCapture();
  // Link-only mode (a conversation or single message) shows just the public link; embed (runner) mode also shows the live iframe snippet.
  const isLinkMode = !!conversationId;
  const [isSharing, setIsSharing] = useState(true);
  const [shareId, setShareId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState<'link' | 'embed' | null>(null);
  // "Include my prompts" — whole-conversation shares of agent conversations only. The share is created on mount with prompts hidden; toggling updates the record (owner-auth).
  const [includePrompts, setIncludePrompts] = useState(false);
  const [isTogglingPrompts, setIsTogglingPrompts] = useState(false);
  const currentConversation = useSelector((state: RootState) => state.aiChat.currentConversation);
  const isAgentConversationShare =
    isLinkMode &&
    !messageId &&
    !!currentConversation &&
    currentConversation.id === conversationId &&
    !!currentConversation.agentScheduleRunnerId;

  const handleToggleIncludePrompts = async (next: boolean) => {
    if (!shareId || isTogglingPrompts) return;
    setIncludePrompts(next);
    setIsTogglingPrompts(true);
    try {
      await ShareService.setShareIncludePrompts(shareId, next);
    } catch (err) {
      console.error('Failed to update includePrompts:', err);
      setIncludePrompts(!next);
    } finally {
      setIsTogglingPrompts(false);
    }
  };

  // Create the share once, on mount (ModalGlobal mounts this component only while the modal is open).
  useEffect(() => {
    if (!runnerId && !isLinkMode) {
      setError(true);
      setIsSharing(false);
      return;
    }
    let cancelled = false;
    setIsSharing(true);
    setError(false);
    (async () => {
      try {
        const { shareId: id } = isLinkMode
          ? await ShareService.createShare(conversationId!, messageId)
          : await ShareService.createScheduleShare(runnerId!);
        if (cancelled) return;
        setShareId(id);
        if (isLinkMode) {
          capture(POSTHOG_EVENTS.SHARE_LINK_CREATED, {
            scope: messageId ? 'message' : 'conversation',
            conversation_id: conversationId,
          });
        } else {
          capture(POSTHOG_EVENTS.SHARE_EMBED_CREATED, { runner_id: runnerId });
        }
      } catch (err) {
        console.error('Failed to create share:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsSharing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runnerId, conversationId, messageId, isLinkMode, capture]);

  const handleCopy = async (kind: 'link' | 'embed', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
      if (kind === 'embed') {
        capture(POSTHOG_EVENTS.SHARE_EMBED_COPIED, { share_id: shareId });
      } else {
        capture(POSTHOG_EVENTS.SHARE_LINK_COPIED, {
          share_id: shareId,
          scope: isLinkMode ? (messageId ? 'message' : 'conversation') : 'runner',
        });
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const publicLink = shareId ? buildShareUrl(shareId) : '';
  const embedSnippet = shareId ? buildEmbedSnippet(shareId) : '';

  return (
    <ModalBase
      title={isLinkMode ? (messageId ? 'Share message' : 'Share conversation') : 'Share / embed live result'}
      icon={
        isLinkMode ? (
          <FiShare2 className="h-5 w-5 text-eai-primary-500" />
        ) : (
          <FiCode className="h-5 w-5 text-eai-primary-500" />
        )
      }
      onClose={onClose}
      size="sm"
    >
      {isSharing ? (
        <div className="flex items-center justify-center py-10 font-inter text-eai-body-sm text-eai-ink-subtle">
          <FiLoader className="mr-2 h-4 w-4 animate-spin" /> Generating share link…
        </div>
      ) : error ? (
        <p className="py-6 text-center font-inter text-eai-body-sm text-eai-danger-text">
          Could not create the share link. Please try again.
        </p>
      ) : (
        <>
          {/* Warning bubble */}
          <span className="inline-flex items-start gap-1.5 rounded-eai-card bg-eai-brand-peach px-3 py-1.5 font-inter text-eai-body-xs text-eai-ink ring-1 ring-eai-warning-text/25">
            <FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {isLinkMode ? SHARE_WARNING : LIVE_SHARE_WARNING}
          </span>

          {/* Public link */}
          <div className="mt-4">
            <label htmlFor="share-public-link" className="eai-meta-label mb-1.5 block">Public link</label>
            <div className="flex items-center gap-2">
              <input
                id="share-public-link"
                type="text"
                readOnly
                value={publicLink}
                onFocus={(e) => e.target.select()}
                className="eai-input-readonly min-w-0 flex-1"
              />
              <button
                type="button"
                onClick={() => handleCopy('link', publicLink)}
                aria-label="Copy public link"
                title="Copy link"
                className="eai-btn-icon shrink-0"
              >
                {copied === 'link' ? <FiCheck className="h-3.5 w-3.5 text-eai-primary-accent" /> : <FiCopy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          {/* Include-prompts toggle — whole-conversation shares of agent conversations only. Agent shares hide the owner's prompts by default; this opts the share in. */}
          {isAgentConversationShare && (
            <label className="mt-3 flex cursor-pointer items-start gap-2 font-inter text-eai-body-xs text-eai-ink-muted">
              <input
                type="checkbox"
                checked={includePrompts}
                disabled={!shareId || isTogglingPrompts}
                onChange={(e) => handleToggleIncludePrompts(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-eai-cta-primary disabled:opacity-50"
              />
              <span>
                Include my prompts. Unchecked, viewers see only the agent&apos;s responses.
              </span>
            </label>
          )}

          {/* Embed snippet — runner embed mode only (conversation/message shares are link-only). */}
          {!isLinkMode && (
          <div className="mt-4">
            <label htmlFor="share-embed-snippet" className="eai-meta-label mb-1.5 block">Embed (iframe)</label>
            <div className="flex items-start gap-2">
              <textarea
                id="share-embed-snippet"
                readOnly
                rows={4}
                value={embedSnippet}
                onFocus={(e) => e.target.select()}
                className="eai-input-readonly h-auto min-w-0 flex-1 resize-none py-2 font-mono text-eai-body-xs"
              />
              <button
                type="button"
                onClick={() => handleCopy('embed', embedSnippet)}
                aria-label="Copy embed snippet"
                title="Copy embed code"
                className="eai-btn-icon mt-1 shrink-0"
              >
                {copied === 'embed' ? <FiCheck className="h-3.5 w-3.5 text-eai-primary-accent" /> : <FiCopy className="h-3.5 w-3.5" />}
              </button>
            </div>
            <p className="mt-1.5 font-inter text-eai-body-xs text-eai-ink-faint">The embed refreshes automatically as the schedule runs.</p>
          </div>
          )}
        </>
      )}
    </ModalBase>
  );
};

export default ShareLinkModal;
